// src/components/dashboard/FiltersBar/ResultFilter.jsx
import React from 'react';
import { TextField, MenuItem, Box, alpha, useTheme, Typography, Chip } from '@mui/material';
import { Assessment, TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { setResultFilter } from '../../../store/slices/dashboardSlice';

// Common result types for betting
const RESULT_OPTIONS = [
  { value: '', label: 'All Results', icon: Assessment, color: 'text.secondary' },
  { value: 'win', label: 'Wins', icon: TrendingUp, color: 'success.main' },
  { value: 'loss', label: 'Losses', icon: TrendingDown, color: 'error.main' },
  { value: 'draw', label: 'Draws', icon: Remove, color: 'warning.main' },
  { value: 'void', label: 'Void', icon: Remove, color: 'text.secondary' },
  { value: 'pending', label: 'Pending', icon: Assessment, color: 'info.main' },
];

const ResultFilter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { resultFilter } = useSelector((state) => state.dashboard.filters);

  const handleChange = (event) => {
    dispatch(setResultFilter(event.target.value));
  };

  const selectedResult = RESULT_OPTIONS.find(r => r.value === resultFilter);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Assessment sx={{ color: 'primary.main', fontSize: '1rem' }} />
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.875rem'
          }}
        >
          Result Type
        </Typography>
        {resultFilter && (
          <Chip
            size="small"
            label={selectedResult?.label || resultFilter}
            color="info"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      <TextField
        select
        value={resultFilter}
        onChange={handleChange}
        size="medium"
        fullWidth
        sx={{ 
          minWidth: { xs: '100%', sm: 160 },
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            background: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          },
          '& .MuiSelect-select': {
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            const option = RESULT_OPTIONS.find(r => r.value === selected);
            const Icon = option?.icon || Assessment;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ 
                  fontSize: '1rem', 
                  color: option?.color || 'text.secondary'
                }} />
                <Typography 
                  variant="body2" 
                  fontWeight={600}
                  sx={{ color: option?.color || 'text.primary' }}
                >
                  {option?.label || 'All Results'}
                </Typography>
              </Box>
            );
          },
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                backdropFilter: 'blur(10px)',
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&.Mui-selected': {
                    background: alpha(theme.palette.primary.main, 0.2),
                    fontWeight: 600,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.3),
                    },
                  },
                },
              },
            },
          },
        }}
      >
        {RESULT_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <MenuItem key={option.value} value={option.value}>
              <Icon sx={{ 
                fontSize: '1rem', 
                color: option.color
              }} />
              <Typography 
                variant="body2" 
                fontWeight={600}
                sx={{ color: option.color }}
              >
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </TextField>
    </Box>
  );
};

export default ResultFilter;